import React, { useState, useEffect } from "react";
import {
  Users,
  Search,
  Filter,
  Eye,
  Power,
  PowerOff,
  Trash2,
  CheckCircle,
  Clock,
  MapPin,
  Phone,
  Mail,
  User,
  Car,
  Activity,
  RefreshCw,
  ChevronDown,
  AlertTriangle,
  X,
  Loader2,
  Shield,
  GraduationCap,
  UserCheck,
  MoreVertical,
} from "lucide-react";
import agentsService from "../../../api/AgentsService";
import { toast } from "react-toastify";
import Loader from "../../../components/misc/Loader";
import { useNavigate } from "react-router-dom";

const Agents = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalData, setModalData] = useState({});
  const [modalAction, setModalAction] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // View details modal state
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Dropdown menu state
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({});

  // Filter and search agents
  const filteredAgents = agents
    .filter((agent) => {
      const matchesSearch =
        agent.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.agent_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.location?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "verified" &&
          (agent.email_verified_at || agent.phone_number_verified_at)) ||
        (statusFilter === "pending" &&
          !agent.email_verified_at &&
          !agent.phone_number_verified_at);

      const matchesRegion =
        regionFilter === "all" ||
        agent.region?.toLowerCase().includes(regionFilter.toLowerCase());

      return matchesSearch && matchesStatus && matchesRegion;
    })
    .sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "name":
          aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
          bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
          break;
        case "email":
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case "location":
          aValue = a.location?.toLowerCase() || "";
          bValue = b.location?.toLowerCase() || "";
          break;
        case "status":
          aValue = a.email_verified_at || a.phone_number_verified_at ? 1 : 0;
          bValue = b.email_verified_at || b.phone_number_verified_at ? 1 : 0;
          break;
        default:
          aValue = a[sortBy] || "";
          bValue = b[sortBy] || "";
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  useEffect(() => {
    const fetchAgents = async () => {
      setLoading(true);
      try {
        const response = await agentsService.getAgents();
        if (response.data?.status_code === 200) {
          setAgents(response.data.data);
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        toast.error(error.message || "An error occurred while fetching agents");
      } finally {
        setLoading(false);
      }
    };
    fetchAgents();
  }, []);
  console.log("agents", agents)

  // Calculate stats
  const totalAgents = agents.length;
  const verifiedAgents = agents.filter(
    (agent) => agent.email_verified_at || agent.phone_number_verified_at
  ).length;
  const ridersCount = agents.filter(
    (agent) => agent.agent_type === "rider"
  ).length;
  const driversCount = agents.filter(
    (agent) => agent.agent_type === "driver"
  ).length;
  const generalAgentsCount = agents.filter(
    (agent) => agent.agent_type === "general"
  ).length;
  const directAgentsCount = agents.filter(
    (agent) => agent.agent_type === "direct"
  ).length;

  const getStatusBadge = (agent) => {
    if (agent.email_verified_at || agent.phone_number_verified_at) {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
          <CheckCircle className="mr-1 w-3 h-3" />
          Verified
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-800 bg-yellow-100 rounded-full">
          <Clock className="mr-1 w-3 h-3" />
          Pending
        </span>
      );
    }
  };

  const getAgentTypeBadge = (agentType) => {
    const getTypeConfig = (type) => {
      switch (type) {
        case "rider":
          return {
            icon: Car,
            label: "Rider Agent",
            bgColor: "bg-blue-100",
            textColor: "text-blue-800",
            iconColor: "text-blue-600",
          };
        case "driver":
          return {
            icon: Car,
            label: "Driver Agent",
            bgColor: "bg-blue-100",
            textColor: "text-blue-800",
            iconColor: "text-blue-600",
          };
        case "general":
          return {
            icon: User,
            label: "General Agent",
            bgColor: "bg-purple-100",
            textColor: "text-purple-800",
            iconColor: "text-purple-600",
          };
        case "direct":
          return {
            icon: Users,
            label: "Direct Agent",
            bgColor: "bg-emerald-100",
            textColor: "text-emerald-800",
            iconColor: "text-emerald-600",
          };
        default:
          return null;
      }
    };

    const config = getTypeConfig(agentType);
    if (!config) return null;

    const IconComponent = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap ${config.bgColor} ${config.textColor}`}
      >
        <IconComponent className={`w-3 h-3 mr-1 ${config.iconColor}`} />
        {config.label}
      </span>
    );
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const response = await agentsService.getAgents();
      if (response.data?.status_code === 200) {
        setAgents(response.data.data);
        console.log("agents", agents);
        toast.success("Agents data refreshed successfully");
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(
        error.response.data.message ||
          "An error occurred while refreshing agents"
      );
    } finally {
      setLoading(false);
    }
  };

  // Action handlers
  const handleStatusChange = (agent, action) => {
    setModalData(agent);
    setModalAction(action);
    setShowConfirmModal(true);
  };

  const handleRemoveAgent = (agent) => {
    setModalData(agent);
    setModalAction("remove");
    setShowConfirmModal(true);
  };

  const handleViewAgent = (agent) => {
    setSelectedAgent(agent);
    setShowViewModal(true);
  };

  const handleViewAgentStudents = (agent) => {
    navigate(`/admin/agents/registered-students?agent=${agent.slug}`);
  };

  const handleViewAgentClients = (agent) => {
    navigate(`/admin/agents/registered-clients?agent=${agent.slug}`);
  };

  const toggleDropdown = (agentSlug, event) => {
    const newState = openDropdown === agentSlug ? null : agentSlug;
    setOpenDropdown(newState);
    
    if (newState && event && event.currentTarget) {
      const buttonElement = event.currentTarget;
      
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        if (!buttonElement) return;
        
        const buttonRect = buttonElement.getBoundingClientRect();
        const spaceBelow = window.innerHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        const dropdownHeight = 280;
        const padding = 20;
        const minRequiredSpace = dropdownHeight + padding;
        const shouldDropUp = spaceBelow < minRequiredSpace && spaceAbove >= minRequiredSpace;
        
        setDropdownPosition(prev => ({
          ...prev,
          [agentSlug]: shouldDropUp ? 'up' : 'down'
        }));
      });
    } else {
      // Clean up position when closing
      setDropdownPosition(prev => {
        const newPos = { ...prev };
        delete newPos[agentSlug];
        return newPos;
      });
    }
  };

  const closeDropdown = () => {
    setOpenDropdown(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.dropdown-container')) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedAgent(null);
  };

  const closeModal = () => {
    setShowConfirmModal(false);
    setModalData({});
    setModalAction("");
    setActionLoading(false);
  };

  // Execute modal action
  const executeAction = async () => {
    setActionLoading(true);
    try {
      let response;

      if (modalAction === "remove") {
        response = await agentsService.removeAgentAccount(modalData.slug);
      } else {
        // Status change (activate/deactivate)
        response = await agentsService.agentStatusChange(
          modalData.slug,
          {},
          modalAction
        );
      }

      if (response.status) {
        toast.success(response.message || "Action completed successfully");
        // Refresh the agents list
        await handleRefresh();
        closeModal();
      } else {
        toast.error(response.message || "Action failed");
      }
    } catch (error) {
      toast.error(
        error.message || "An error occurred while processing the action"
      );
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <Loader fullScreen={true} text="Loading agents..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Container */}
      <div className="px-3 py-4 space-y-4 sm:px-4 sm:py-6 lg:px-6 lg:py-8 sm:space-y-6 lg:space-y-8">
        {/* Header */}
        <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div className="flex-shrink-0">
            <h1 className="flex items-center text-xl font-bold text-gray-900 sm:text-2xl lg:text-3xl xl:text-4xl">
              <Users className="flex-shrink-0 mr-2 w-6 h-6 text-emerald-600 sm:w-7 sm:h-7 lg:w-8 lg:h-8 sm:mr-3" />
              <span className="truncate">Agents Management</span>
            </h1>
            <p className="mt-1 text-sm text-gray-600 sm:text-base sm:mt-2">
              Manage and monitor all platform agents
            </p>
          </div>

          {/* Mobile Header Actions */}
          <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
            <button
              onClick={handleRefresh}
              className="bg-gray-100 text-gray-700 px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors flex items-center justify-center min-h-[44px] w-full sm:w-auto touch-manipulation"
            >
              <RefreshCw className="flex-shrink-0 mr-2 w-4 h-4" />
              <span className="hidden xs:inline">Refresh</span>
              <span className="xs:hidden">Reload</span>
            </button>
            <button
              disabled
              className="bg-gray-400 text-white px-4 py-2.5 sm:px-6 sm:py-3 rounded-lg cursor-not-allowed opacity-70 flex items-center justify-center shadow-sm min-h-[44px] w-full sm:w-auto font-medium touch-manipulation"
            >
              <Users className="flex-shrink-0 mr-2 w-4 h-4" />
              <span>Add New Agent (Coming Soon)</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-3 xs:grid-cols-2 lg:grid-cols-4 sm:gap-4 lg:gap-6">
          <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow duration-300 sm:rounded-xl sm:p-5 lg:p-6 hover:shadow-md">
            <div className="flex justify-between items-center">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-600 truncate sm:text-sm">
                  Total Agents
                </p>
                <p className="mt-1 text-xl font-bold text-gray-900 sm:text-2xl lg:text-3xl">
                  {totalAgents}
                </p>
                <div className="flex items-center mt-1">
                  <Activity className="flex-shrink-0 mr-1 w-3 h-3 text-green-500 sm:w-4 sm:h-4" />
                  <span className="text-xs text-green-600 sm:text-sm">
                    Active
                  </span>
                </div>
              </div>
              <div className="flex-shrink-0 p-2 ml-3 bg-blue-100 rounded-lg sm:p-3">
                <Users className="w-4 h-4 text-blue-600 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
              </div>
            </div>
          </div>

          <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow duration-300 sm:rounded-xl sm:p-5 lg:p-6 hover:shadow-md">
            <div className="flex justify-between items-center">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-600 truncate sm:text-sm">
                  Verified
                </p>
                <p className="mt-1 text-xl font-bold text-green-600 sm:text-2xl lg:text-3xl">
                  {verifiedAgents}
                </p>
                <p className="mt-1 text-xs text-gray-500 sm:text-sm">
                  {totalAgents > 0
                    ? Math.round((verifiedAgents / totalAgents) * 100)
                    : 0}
                  % of total
                </p>
              </div>
              <div className="flex-shrink-0 p-2 ml-3 bg-green-100 rounded-lg sm:p-3">
                <CheckCircle className="w-4 h-4 text-green-600 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
              </div>
            </div>
          </div>

          <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow duration-300 sm:rounded-xl sm:p-5 lg:p-6 hover:shadow-md">
            <div className="flex justify-between items-center">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-600 truncate sm:text-sm">
                  Transportation
                </p>
                <p className="mt-1 text-xl font-bold text-blue-600 sm:text-2xl lg:text-3xl">
                  {ridersCount + driversCount}
                </p>
                <p className="flex items-center mt-1 space-x-2 text-xs text-gray-500 sm:text-sm">
                  <span>{ridersCount} riders</span>
                  <span>•</span>
                  <span>{driversCount} drivers</span>
                </p>
              </div>
              <div className="flex-shrink-0 p-2 ml-3 bg-blue-100 rounded-lg sm:p-3">
                <Car className="w-4 h-4 text-blue-600 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
              </div>
            </div>
          </div>

          {/* Additional Agents Types Card */}
          <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow duration-300 sm:rounded-xl sm:p-5 lg:p-6 hover:shadow-md">
            <div className="flex justify-between items-center">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-600 truncate sm:text-sm">
                  Sales Agents
                </p>
                <p className="mt-1 text-xl font-bold text-emerald-600 sm:text-2xl lg:text-3xl">
                  {generalAgentsCount + directAgentsCount}
                </p>
                <p className="flex items-center mt-1 space-x-2 text-xs text-gray-500 sm:text-sm">
                  <span>{generalAgentsCount} general</span>
                  <span>•</span>
                  <span>{directAgentsCount} direct</span>
                </p>
              </div>
              <div className="flex-shrink-0 p-2 ml-3 bg-emerald-100 rounded-lg sm:p-3">
                <Users className="w-4 h-4 text-emerald-600 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm sm:rounded-xl sm:p-6">
          {/* Mobile Filter Toggle */}
          <div className="flex justify-between items-center mb-4 lg:hidden">
            <h3 className="flex items-center text-base font-semibold text-gray-900">
              <Filter className="mr-2 w-4 h-4 text-emerald-600" />
              Filters
            </h3>
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="flex items-center px-3 py-2 text-sm bg-gray-100 rounded-lg transition-colors hover:bg-gray-200 touch-manipulation"
            >
              <span>Filters</span>
              <ChevronDown
                className={`w-4 h-4 ml-2 transition-transform ${
                  showMobileFilters ? "rotate-180" : ""}`}
              />
            </button>
          </div>

          {/* Desktop Filter Header */}
          <div className="hidden justify-between items-center mb-6 lg:flex">
            <h3 className="flex items-center text-lg font-semibold text-gray-900">
              <Filter className="mr-2 w-5 h-5 text-emerald-600" />
              Filters & Search
            </h3>
            <button className="text-sm font-medium text-emerald-600 transition-colors hover:text-emerald-700">
              Export Data
            </button>
          </div>

          {/* Filter Controls */}
          <div
            className={`space-y-4 ${
              showMobileFilters ? "block" : "hidden lg:block"
            }`}
          >
            {/* Search */}
            <div className="relative">
              <Search className="absolute top-3 left-3 w-4 h-4 text-gray-400 sm:w-5 sm:h-5" />
              <input
                type="text"
                placeholder="Search agents by name, email, code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="py-3 pr-4 pl-10 w-full text-sm rounded-lg border border-gray-300 transition-all sm:text-base focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 sm:gap-4">
              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-3 text-sm bg-white rounded-lg border border-gray-300 appearance-none sm:px-4 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="verified">Verified Only</option>
                <option value="pending">Pending Only</option>
              </select>

              {/* Region Filter */}
              <select
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                className="px-3 py-3 text-sm bg-white rounded-lg border border-gray-300 appearance-none sm:px-4 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="all">All Regions</option>
                <option value="greater accra">Greater Accra</option>
                <option value="greater-accra">Greater-Accra</option>
              </select>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-3 text-sm bg-white rounded-lg border border-gray-300 appearance-none sm:px-4 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="name">Sort by Name</option>
                <option value="email">Sort by Email</option>
                <option value="location">Sort by Location</option>
                <option value="status">Sort by Status</option>
              </select>

              {/* Sort Order */}
              <button
                onClick={() =>
                  setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                }
                className="flex justify-center items-center px-3 py-3 text-sm bg-gray-100 rounded-lg border border-gray-300 transition-colors sm:px-4 hover:bg-gray-200 active:bg-gray-300 touch-manipulation"
              >
                {sortOrder === "asc" ? "↓" : "↑"}{" "}
                {sortOrder === "asc" ? "Ascending" : "Descending"}
              </button>
            </div>
          </div>

          {/* Clear Filters Button */}
          {searchTerm || statusFilter !== "all" || regionFilter !== "all" ? (
            <div className="flex justify-end mt-4">
              <button
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("all");
                  setRegionFilter("all");
                }}
                className="px-3 py-2 text-sm text-red-600 rounded-lg transition-colors hover:text-red-700 hover:bg-red-50"
              >
                Clear Filters
              </button>
            </div>
          ) : null}
        </div>

        {/* Agents Display */}
        <div className="overflow-hidden bg-white rounded-lg border shadow-sm sm:rounded-xl border-gray-205">
          {/* Table Header */}
          <div className="px-3 py-3 bg-gray-50 border-b border-gray-200 sm:px-4 lg:px-6 sm:py-4">
            <div className="flex justify-between items-center">
              <h3 className="text-base font-semibold text-gray-900 sm:text-lg">
                Agent List ({filteredAgents.length}{" "}
                {filteredAgents.length === 1 ? "agent" : "agents"} found)
              </h3>
              <div className="hidden text-xs text-gray-500 sm:block sm:text-sm">
                Showing {filteredAgents.length} of {totalAgents} agents
              </div>
            </div>
          </div>

          {/* Desktop View */}
          <div className="hidden overflow-x-auto lg:block">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
                    Agent Details
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
                    Contact Info
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
                    Location
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
                    Verification
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
                    Type
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
                    Agent Code
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-right text-gray-500 uppercase whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAgents.map((agent) => (
                  <tr
                    key={agent.slug}
                    className="transition-colors duration-150 hover:bg-gray-50"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full shadow-sm">
                          <span className="text-sm font-medium text-white">
                            {agent.first_name.charAt(0)}
                            {agent.last_name.charAt(0)}
                          </span>
                        </div>
                        <div className="ml-4 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">
                            {agent.first_name} {agent.last_name}
                          </div>
                          <div className="flex items-center text-sm text-gray-500 truncate">
                            {agent.gender ? (
                              <>
                                <span className="flex-shrink-0 mr-2 w-2 h-2 bg-gray-300 rounded-full"></span>
                                {`${
                                  agent.gender.charAt(0).toUpperCase() +
                                  agent.gender.slice(1)
                                }`}
                              </>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                                Gender N/A
                              </span>
                            )}
                            {agent.dob && (
                              <span className="ml-2 text-gray-400 truncate">
                                • Born{" "}
                                {new Date(
                                  agent.dob.slice(0, 4),
                                  agent.dob.slice(4, 6) - 1,
                                  agent.dob.slice(6, 8)
                                ).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {agent.email ? (
                          <div className="flex items-center text-sm text-gray-900 truncate">
                            <Mail className="flex-shrink-0 mr-2 w-3 h-3 text-gray-400" />
                            <span className="truncate">{agent.email}</span>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <Mail className="flex-shrink-0 mr-2 w-3 h-3 text-gray-400" />
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                              Not Applicable
                            </span>
                          </div>
                        )}
                        <div className="flex items-center text-sm text-gray-500 truncate">
                          <Phone className="flex-shrink-0 mr-2 w-3 h-3 text-gray-400" />
                          <span className="truncate">{agent.phone_number}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center min-w-0">
                        <MapPin className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                        <div className="min-w-0">
                          {agent.location ? (
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {agent.location}
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                              Not Applicable
                            </span>
                          )}
                          {agent.region ? (
                            <div className="text-sm text-gray-500 truncate">
                              {agent.region}
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500 mt-1">
                              Region N/A
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="space-y-1">
                        {getStatusBadge(agent)}
                        <div className="text-xs text-gray-500 whitespace-nowrap">
                          Email {agent.email_verified_at ? "✓" : "✗"} | Phone{" "}
                          {agent.phone_number_verified_at ? "✓" : "✗"}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="flex items-center space-x-2">
                        {getAgentTypeBadge(agent.agent_type) || (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 rounded-md">
                            Not Applicable
                          </span>
                        )}
                        {agent.license_plate_number && (
                          <span className="px-2 py-1 font-mono text-xs text-gray-500 whitespace-nowrap bg-gray-100 rounded">
                            {agent.license_plate_number}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 font-mono text-sm font-semibold text-emerald-800 bg-emerald-100 rounded-lg">
                        {agent.agent_code}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="relative dropdown-container">
                        <button
                          onClick={(e) => toggleDropdown(agent.slug, e)}
                          className="p-2 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-50 touch-manipulation"
                          title="Actions"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        {openDropdown === agent.slug && (
                          <div className={`absolute right-0 z-50 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 ${
                            dropdownPosition[agent.slug] === 'up' 
                              ? 'bottom-full mb-2' 
                              : 'top-full mt-2'
                          }`}>
                            <button
                              onClick={() => {
                                handleViewAgent(agent);
                                closeDropdown();
                              }}
                              className="flex items-center px-4 py-2 space-x-2 w-full text-sm text-left text-gray-700 transition-colors hover:bg-emerald-50 hover:text-emerald-600"
                            >
                              <Eye className="w-4 h-4" />
                              <span>View Details</span>
                            </button>
                            <button
                              onClick={() => {
                                handleViewAgentStudents(agent);
                                closeDropdown();
                              }}
                              className="flex items-center px-4 py-2 space-x-2 w-full text-sm text-left text-gray-700 transition-colors hover:bg-blue-50 hover:text-blue-600"
                            >
                              <GraduationCap className="w-4 h-4" />
                              <span>View Students</span>
                            </button>
                            <button
                              onClick={() => {
                                handleViewAgentClients(agent);
                                closeDropdown();
                              }}
                              className="flex items-center px-4 py-2 space-x-2 w-full text-sm text-left text-gray-700 transition-colors hover:bg-purple-50 hover:text-purple-600"
                            >
                              <UserCheck className="w-4 h-4" />
                              <span>View Clients</span>
                            </button>
                            <div className="my-1 border-t border-gray-200"></div>
                            <button
                              onClick={() => {
                                handleStatusChange(
                                  agent,
                                  agent.status === 1 ? "deactivate" : "activate"
                                );
                                closeDropdown();
                              }}
                              className={`w-full px-4 py-2 text-left text-sm flex items-center space-x-2 transition-colors ${
                                agent.status === 1
                                  ? "text-red-600 hover:bg-red-50"
                                  : "text-green-600 hover:bg-green-50"
                              }`}
                            >
                              {agent.status === 1 ? (
                                <PowerOff className="w-4 h-4" />
                              ) : (
                                <Power className="w-4 h-4" />
                              )}
                              <span>
                                {agent.status === 1
                                  ? "Deactivate Account"
                                  : "Activate Account"}
                              </span>
                            </button>
                            <div className="my-1 border-t border-gray-200"></div>
                            <button
                              onClick={() => {
                                handleRemoveAgent(agent);
                                closeDropdown();
                              }}
                              className="flex items-center px-4 py-2 space-x-2 w-full text-sm text-left text-red-600 transition-colors hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Remove Account</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="divide-y divide-gray-200 lg:hidden">
            {filteredAgents.map((agent) => (
              <div
                key={agent.slug}
                className="p-4 transition-colors hover:bg-gray-50"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex flex-1 items-center min-w-0">
                    <div className="flex flex-shrink-0 justify-center items-center mr-3 w-10 h-10 bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full shadow-sm">
                      <span className="text-sm font-medium text-white">
                        {agent.first_name.charAt(0)}
                        {agent.last_name.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-base font-semibold text-gray-900 truncate">
                        {agent.first_name} {agent.last_name}
                      </div>
                      <div className="flex items-center text-sm text-gray-500 truncate">
                        {getStatusBadge(agent)}
                      </div>
                    </div>
                  </div>
                  <div className="relative ml-2 dropdown-container">
                    <button
                      onClick={(e) => toggleDropdown(agent.slug, e)}
                      className="p-2 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-50 touch-manipulation"
                      title="Actions"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {openDropdown === agent.slug && (
                      <div className={`absolute right-0 z-50 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 ${
                        dropdownPosition[agent.slug] === 'up' 
                          ? 'bottom-full mb-2' 
                          : 'top-full mt-2'
                      }`}>
                        <button
                          onClick={() => {
                            handleViewAgent(agent);
                            closeDropdown();
                          }}
                          className="flex items-center px-4 py-2 space-x-2 w-full text-sm text-left text-gray-700 transition-colors hover:bg-emerald-50 hover:text-emerald-600"
                        >
                          <Eye className="w-4 h-4" />
                          <span>View Details</span>
                        </button>
                        <button
                          onClick={() => {
                            handleViewAgentStudents(agent);
                            closeDropdown();
                          }}
                          className="flex items-center px-4 py-2 space-x-2 w-full text-sm text-left text-gray-700 transition-colors hover:bg-blue-50 hover:text-blue-600"
                        >
                          <GraduationCap className="w-4 h-4" />
                          <span>View Students</span>
                        </button>
                        <button
                          onClick={() => {
                            handleViewAgentClients(agent);
                            closeDropdown();
                          }}
                          className="flex items-center px-4 py-2 space-x-2 w-full text-sm text-left text-gray-700 transition-colors hover:bg-purple-50 hover:text-purple-600"
                        >
                          <UserCheck className="w-4 h-4" />
                          <span>View Clients</span>
                        </button>
                        <div className="my-1 border-t border-gray-200"></div>
                        <button
                          onClick={() => {
                            handleStatusChange(
                              agent,
                              agent.status === 1 ? "deactivate" : "activate"
                            );
                            closeDropdown();
                          }}
                          className={`w-full px-4 py-2 text-left text-sm flex items-center space-x-2 transition-colors ${
                            agent.status === 1
                              ? "text-red-600 hover:bg-red-50"
                              : "text-green-600 hover:bg-green-50"
                          }`}
                        >
                          {agent.status === 1 ? (
                            <PowerOff className="w-4 h-4" />
                          ) : (
                            <Power className="w-4 h-4" />
                          )}
                          <span>
                            {agent.status === 1
                              ? "Deactivate Account"
                              : "Activate Account"}
                          </span>
                        </button>
                        <div className="my-1 border-t border-gray-200"></div>
                        <button
                          onClick={() => {
                            handleRemoveAgent(agent);
                            closeDropdown();
                          }}
                          className="flex items-center px-4 py-2 space-x-2 w-full text-sm text-left text-red-600 transition-colors hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Remove Account</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  {/* Contact Info */}
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <Mail className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                      {agent.email ? (
                        <span className="text-gray-900 truncate">
                          {agent.email}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                          Not Applicable
                        </span>
                      )}
                    </div>
                    <div className="flex items-center text-sm">
                      <Phone className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                      <span className="text-gray-900 truncate">
                        {agent.phone_number}
                      </span>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <MapPin className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                      {agent.location ? (
                        <span className="text-gray-900 truncate">
                          {agent.location}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                          Not Applicable
                        </span>
                      )}
                    </div>
                    {agent.region ? (
                      <div className="ml-6 text-sm text-gray-500 truncate">
                        {agent.region}
                      </div>
                    ) : (
                      <div className="ml-6">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                          Region N/A
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Bottom Info */}
                  <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                    <div className="flex flex-wrap gap-2">
                      {getAgentTypeBadge(agent.agent_type) || (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 rounded-md">
                          Type N/A
                        </span>
                      )}
                      <span className="px-2 py-1 font-mono text-sm font-semibold text-emerald-800 bg-emerald-100 rounded">
                        {agent.agent_code}
                      </span>
                      {agent.license_plate_number && (
                        <span className="px-2 py-1 font-mono text-xs text-gray-500 bg-gray-100 rounded">
                          {agent.license_plate_number}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredAgents.length === 0 && !loading && (
            <div className="py-12 text-center lg:py-16">
              <Users className="mx-auto w-12 h-12 text-gray-300 lg:h-16 lg:w-16" />
              <h3 className="mt-4 text-base font-medium text-gray-900 lg:text-lg">
                No agents found
              </h3>
              <p className="px-4 mx-auto mt-2 max-w-sm text-sm text-gray-500 lg:text-sm">
                {searchTerm || statusFilter !== "all" || regionFilter !== "all"
                  ? "Try adjusting your search or filter criteria to find agents."
                  : "No agents have been registered yet. Add your first agent to get started."}
              </p>
              {!searchTerm &&
                statusFilter === "all" &&
                regionFilter === "all" && (
                  <button
                    disabled
                    className="mt-4 bg-gray-400 text-white px-4 py-2.5 sm:px-6 sm:py-3 rounded-lg cursor-not-allowed opacity-70 text-sm font-medium touch-manipulation"
                  >
                    Add First Agent (Coming Soon)
                  </button>
                )}
            </div>
          )}
        </div>

        {/* Mobile Stats */}
        <div className="text-center lg:hidden">
          <div className="inline-block px-3 py-2 text-xs text-gray-500 bg-gray-100 rounded-lg">
            Showing {filteredAgents.length} of {totalAgents} agents
          </div>
        </div>

        {/* Pagination placeholder */}
        {filteredAgents.length > 0 && (
          <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:rounded-xl sm:p-4">
            <div className="flex flex-col justify-between items-center space-y-2 sm:flex-row sm:space-y-0">
              <div className="text-xs text-center text-gray-500 sm:text-sm sm:text-left">
                Showing 1 to {filteredAgents.length} of {filteredAgents.length}{" "}
                results
              </div>
              <div className="flex space-x-2">
                <button className="px-3 py-2 text-xs text-gray-700 bg-gray-100 rounded-lg sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation">
                  Previous
                </button>
                <button className="px-3 py-2 text-xs text-white bg-emerald-600 rounded-lg sm:text-sm touch-manipulation">
                  1
                </button>
                <button className="px-3 py-2 text-xs text-gray-700 bg-gray-100 rounded-lg sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation">
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="flex fixed inset-0 z-50 justify-center items-center">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
              onClick={actionLoading ? undefined : closeModal}
            />

            {/* Modal */}
            <div className="relative p-6 mx-4 w-full max-w-md bg-white rounded-2xl shadow-2xl duration-200 animate-in fade-in-0 zoom-in-95">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div
                  className={`p-3 rounded-full ${
                    modalAction === "remove" ? "bg-red-100" : "bg-blue-100"
                  }`}
                >
                  {modalAction === "remove" ? (
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  ) : (
                    <Power className="w-6 h-6 text-blue-600" />
                  )}
                </div>
                <button
                  onClick={closeModal}
                  disabled={actionLoading}
                  className="p-2 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="mb-6">
                <h3 className="mb-2 text-lg font-semibold text-gray-900">
                  {modalAction === "remove"
                    ? "Remove Agent Account"
                    : modalAction === "activate"
                    ? "Activate Agent Account"
                    : "Deactivate Agent Account"}
                </h3>
                <p className="text-sm leading-relaxed text-gray-600">
                  {modalAction === "remove" ? (
                    <>
                      Are you sure you want to{" "}
                      <span className="font-semibold text-red-600">
                        permanently remove
                      </span>{" "}
                      the account for
                      <span className="font-semibold">
                        {" "}
                        {modalData.first_name} {modalData.last_name}
                      </span>
                      ?
                      <br />
                      <br />
                      <strong>This action cannot be undone</strong> and will
                      remove all agent data from the system.
                    </>
                  ) : modalAction === "activate" ? (
                    <>
                      Are you sure you want to{" "}
                      <span className="font-semibold text-green-600">
                        activate
                      </span>{" "}
                      the account for
                      <span className="font-semibold">
                        {" "}
                        {modalData.first_name} {modalData.last_name}
                      </span>
                      ?
                      <br />
                      <br />
                      This will enable the agent to access their account and
                      perform authorized actions.
                    </>
                  ) : (
                    <>
                      Are you sure you want to{" "}
                      <span className="font-semibold text-orange-600">
                        deactivate
                      </span>{" "}
                      the account for
                      <span className="font-semibold">
                        {" "}
                        {modalData.first_name} {modalData.last_name}
                      </span>
                      ?
                      <br />
                      <br />
                      This will prevent the agent from accessing their account
                      until reactivated.
                    </>
                  )}
                </p>
              </div>

              {/* Agent Info */}
              <div className="p-4 mb-6 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full">
                    <span className="text-sm font-medium text-white">
                      {modalData.first_name?.charAt(0)}
                      {modalData.last_name?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">
                      {modalData.first_name} {modalData.last_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {modalData.email}
                    </div>
                    <div className="font-mono text-xs text-gray-400">
                      Agent: {modalData.agent_code}
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-3">
                <button
                  onClick={closeModal}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-3 font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={executeAction}
                  disabled={actionLoading}
                  className={`flex-1 px-4 py-3 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    modalAction === "remove"
                      ? "bg-red-600 hover:bg-red-700"
                      : modalAction === "activate"
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-orange-600 hover:bg-orange-700"
                  }`}
                >
                  {actionLoading ? (
                    <div className="flex justify-center items-center">
                      <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                      {modalAction === "remove"
                        ? "Removing..."
                        : modalAction === "activate"
                        ? "Activating..."
                        : "Deactivating..."}
                    </div>
                  ) : modalAction === "remove" ? (
                    "Remove Account"
                  ) : modalAction === "activate" ? (
                    "Activate Account"
                  ) : (
                    "Deactivate Account"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Details Modal */}
        {showViewModal && selectedAgent && (
          <div className="flex fixed inset-0 z-50 justify-center items-center">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
              onClick={closeViewModal}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col animate-in fade-in-0 zoom-in-95 duration-200">
              {/* Header */}
              <div className="px-6 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="flex justify-center items-center w-12 h-12 bg-white bg-opacity-20 rounded-full backdrop-blur">
                      <span className="text-lg font-bold text-white">
                        {selectedAgent.first_name.charAt(0)}
                        {selectedAgent.last_name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        {selectedAgent.first_name} {selectedAgent.last_name}
                      </h3>
                      <p className="text-sm text-emerald-100">Agent Details</p>
                    </div>
                  </div>
                  <button
                    onClick={closeViewModal}
                    className="p-2 text-white rounded-lg transition-colors hover:text-emerald-200 hover:bg-white hover:bg-opacity-20"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="overflow-y-auto flex-1 p-6 min-h-0">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                  {/* Left Column - Personal Info */}
                  <div className="space-y-4 lg:col-span-1">
                    {/* Verification Status */}
                    <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                      <h4 className="flex items-center mb-3 font-semibold text-gray-900">
                        <CheckCircle className="mr-2 w-4 h-4 text-green-600" />
                        Verification Status
                      </h4>
                      <div className="space-y-2">
                        <div
                          className={`flex items-center px-3 py-2 rounded-lg ${
                            selectedAgent.email_verified_at
                              ? "bg-green-100"
                              : "bg-red-100"
                          }`}
                        >
                          <div
                            className={`w-3 h-3 rounded-full mr-3 ${
                              selectedAgent.email_verified_at
                                ? "bg-green-500"
                                : "bg-red-500"
                            }`}
                          ></div>
                          <span className="text-sm font-medium">
                            {selectedAgent.email_verified_at
                              ? "Email Verified"
                              : "Email Not Verified"}
                          </span>
                        </div>
                        <div
                          className={`flex items-center px-3 py-2 rounded-lg ${
                            selectedAgent.phone_number_verified_at
                              ? "bg-green-100"
                              : "bg-red-100"
                          }`}
                        >
                          <div
                            className={`w-3 h-3 rounded-full mr-3 ${
                              selectedAgent.phone_number_verified_at
                                ? "bg-green-500"
                                : "bg-red-500"
                            }`}
                          ></div>
                          <span className="text-sm font-medium">
                            {selectedAgent.phone_number_verified_at
                              ? "Phone Verified"
                              : "Phone Not Verified"}
                          </span>
                        </div>
                        {(selectedAgent.email_verified_at ||
                          selectedAgent.phone_number_verified_at) && (
                          <div className="pt-3 mt-3 border-t border-green-200">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="mr-1 w-3 h-3" />
                              Verified Agent
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Account Status */}
                    <div
                      className={`rounded-xl p-4 border ${
                        selectedAgent.status === 1
                          ? "bg-green-50 border-green-200"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <h4 className="flex items-center mb-3 font-semibold text-gray-900">
                        {selectedAgent.status === 1 ? (
                          <Power className="mr-2 w-4 h-4 text-green-600" />
                        ) : (
                          <PowerOff className="mr-2 w-4 h-4 text-gray-500" />
                        )}
                        Account Status
                      </h4>
                      <div className="flex items-center">
                        <div
                          className={`w-3 h-3 rounded-full mr-3 ${
                            selectedAgent.status === 1
                              ? "bg-green-500"
                              : "bg-gray-500"
                          }`}
                        ></div>
                        <span className="text-sm font-medium">
                          {selectedAgent.status === 1
                            ? "Active Account"
                            : "Inactive Account"}
                        </span>
                      </div>
                    </div>

                    {/* Agent Type */}
                    <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <h4 className="flex items-center mb-3 font-semibold text-gray-900">
                        <User className="mr-2 w-4 h-4 text-blue-600" />
                        Agent Type
                      </h4>
                      <div className="space-y-3">
                        {getAgentTypeBadge(selectedAgent.agent_type) || (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 rounded-md">
                            Not Applicable
                          </span>
                        )}

                        {/* Transportation Services */}
                        {(selectedAgent.agent_type === "rider" ||
                          selectedAgent.agent_type === "driver") && (
                          <div className="pt-2 border-t border-blue-200">
                            <p className="mb-2 text-sm font-medium text-gray-700">
                              Transportation Info
                            </p>
                            {selectedAgent.license_plate_number && (
                              <div className="text-sm">
                                <span className="text-gray-600">
                                  License Plate:
                                </span>
                                <span className="px-2 py-1 ml-2 font-mono bg-gray-100 rounded">
                                  {selectedAgent.license_plate_number}
                                </span>
                              </div>
                            )}
                            <div className="mt-1 text-sm text-gray-600">
                              Service:{" "}
                              {selectedAgent.agent_type === "rider"
                                ? "Personal Transportation"
                                : "Commercial Transport"}
                            </div>
                          </div>
                        )}

                        {/* Sales Services */}
                        {(selectedAgent.agent_type === "general-agent" ||
                          selectedAgent.agent_type === "direct-agent") && (
                          <div className="pt-2 border-t border-blue-200">
                            <p className="mb-2 text-sm font-medium text-gray-700">
                              Sales Information
                            </p>
                            <div className="text-sm text-gray-600">
                              Type:{" "}
                              {selectedAgent.agent_type === "general-agent"
                                ? "General Sales Agent"
                                : "Direct Sales Agent"}
                            </div>
                            <div className="mt-1 text-sm text-gray-600">
                              Role:{" "}
                              {selectedAgent.agent_type === "general-agent"
                                ? "Multi-channel Sales"
                                : "Direct Customer Sales"}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Contact & Location */}
                  <div className="space-y-4 lg:col-span-2">
                    {/* Contact Information */}
                    <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                      <h4 className="flex items-center mb-4 font-semibold text-gray-900">
                        <Phone className="mr-2 w-4 h-4 text-emerald-600" />
                        Contact Information
                      </h4>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                              Email Address
                            </label>
                            <div className="flex items-center mt-1">
                              <Mail className="mr-2 w-4 h-4 text-gray-400" />
                              {selectedAgent.email ? (
                                <span className="text-sm font-medium text-gray-900">
                                  {selectedAgent.email}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                                  Not Applicable
                                </span>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                              Phone Number
                            </label>
                            <div className="flex items-center mt-1">
                              <Phone className="mr-2 w-4 h-4 text-gray-400" />
                              <span className="text-sm font-medium text-gray-900">
                                {selectedAgent.phone_number}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                              Agent Code
                            </label>
                            <div className="mt-1">
                              <span className="px-3 py-1 font-mono text-sm font-semibold text-emerald-800 bg-emerald-100 rounded-lg">
                                {selectedAgent.agent_code}
                              </span>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                              Channel
                            </label>
                            <div className="mt-1">
                              {selectedAgent.channel ? (
                                <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-800 capitalize bg-gray-100 rounded-md">
                                  {selectedAgent.channel}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 rounded-md">
                                  Not Applicable
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Location Information */}
                    <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                      <h4 className="flex items-center mb-4 font-semibold text-gray-900">
                        <MapPin className="mr-2 w-4 h-4 text-emerald-600" />
                        Location Information
                      </h4>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                              Location
                            </label>
                            <div className="flex items-center mt-1">
                              <MapPin className="mr-2 w-4 h-4 text-gray-400" />
                              {selectedAgent.location ? (
                                <span className="text-sm font-medium text-gray-900">
                                  {selectedAgent.location}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                                  Not Applicable
                                </span>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                              Region
                            </label>
                            <div className="mt-1">
                              {selectedAgent.region ? (
                                <span className="text-sm font-medium text-gray-900">
                                  {selectedAgent.region}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                                  Not Applicable
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                              Gender
                            </label>
                            <div className="mt-1">
                              {selectedAgent.gender ? (
                                <span className="text-sm font-medium text-gray-900 capitalize">
                                  {selectedAgent.gender}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                                  Not Applicable
                                </span>
                              )}
                            </div>
                          </div>
                          {selectedAgent.dob && (
                            <div>
                              <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                                Date of Birth
                              </label>
                              <div className="mt-1">
                                <span className="text-sm font-medium text-gray-900">
                                  {new Date(
                                    selectedAgent.dob.slice(0, 4),
                                    selectedAgent.dob.slice(4, 6) - 1,
                                    selectedAgent.dob.slice(6, 8)
                                  ).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Document Information */}
                    {(selectedAgent.document_type ||
                      selectedAgent.card_number ||
                      selectedAgent.id_card_front ||
                      selectedAgent.id_card_back) && (
                      <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                        <h4 className="flex items-center mb-4 font-semibold text-gray-900">
                          <Shield className="mr-2 w-4 h-4 text-emerald-600" />
                          Document Information
                        </h4>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div className="space-y-3">
                            {selectedAgent.document_type && (
                              <div>
                                <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                                  Document Type
                                </label>
                                <div className="mt-1">
                                  <span className="text-sm font-medium text-gray-900 uppercase">
                                    {selectedAgent.document_type}
                                  </span>
                                </div>
                              </div>
                            )}
                            {selectedAgent.card_number && (
                              <div>
                                <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                                  Card Number
                                </label>
                                <div className="mt-1">
                                  <span className="px-2 py-1 font-mono text-sm bg-gray-100 rounded">
                                    {selectedAgent.card_number}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="space-y-3">
                            {selectedAgent.issue_date && (
                              <div>
                                <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                                  Issue Date
                                </label>
                                <div className="mt-1">
                                  <span className="text-sm font-medium text-gray-900">
                                    {selectedAgent.issue_date}
                                  </span>
                                </div>
                              </div>
                            )}
                            {selectedAgent.expiry_date && (
                              <div>
                                <label className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                                  Expiry Date
                                </label>
                                <div className="mt-1">
                                  <span className="text-sm font-medium text-gray-900">
                                    {selectedAgent.expiry_date}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end px-6 py-4 bg-gray-50 border-t border-gray-200">
                <button
                  onClick={closeViewModal}
                  className="px-6 py-2 font-medium text-white bg-emerald-600 rounded-lg transition-colors hover:bg-emerald-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Agents;
